/**
 * Desktop sign-in handoff (COS-TBD desktop shell).
 *
 * The coSow desktop app hosts this web app and already holds a Firebase session. It asks
 * the API (POST /api/auth/desktop-handoff) for a Firebase custom token and loads
 * `/so/desktop#desktop=<customToken>`. Before React mounts, main.tsx calls
 * {@link consumeDesktopHandoff}: the hash is removed from the URL first (so the token never
 * lingers in history, bookmarks or a reload), then the token is used to sign in. Any
 * failure is swallowed: the app renders and the normal sign-in page takes over.
 */

const HASH_KEY = 'desktop';

/** The custom token carried in a `#desktop=<token>` hash, or null. Other hash params are ignored. */
export function readDesktopHandoffToken(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const token = new URLSearchParams(raw).get(HASH_KEY);
  return token && token.trim() ? token.trim() : null;
}

/** The hash with the desktop param removed ('' when nothing else is left). */
export function stripDesktopHandoffHash(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  params.delete(HASH_KEY);
  const rest = params.toString();
  return rest ? `#${rest}` : '';
}

export interface DesktopHandoffEnv {
  location: Pick<Location, 'pathname' | 'search' | 'hash'>;
  replaceState: (url: string) => void;
  signIn: (customToken: string) => Promise<unknown>;
}

/**
 * Consume a `#desktop=<token>` hash: clear it, then sign in. Resolves to true when a
 * sign-in succeeded, false when there was no token or the sign-in failed. Never throws.
 */
export async function consumeDesktopHandoff(env: DesktopHandoffEnv): Promise<boolean> {
  const token = readDesktopHandoffToken(env.location.hash);
  if (!token) return false;
  const { pathname, search, hash } = env.location;
  try {
    env.replaceState(`${pathname}${search}${stripDesktopHandoffHash(hash)}`);
  } catch {
    // Clearing the URL is best effort; still sign in.
  }
  try {
    await env.signIn(token);
    return true;
  } catch {
    console.warn('[desktop-handoff] sign-in with the desktop token failed; showing sign-in');
    return false;
  }
}
